// SEO Configuration and Structured Data for Ruben P. Wargner Biography Site

export const SITE_CONFIG = {
  siteName: "Ruben P. Wargner | Infrastructure Leadership",
  siteUrl: "https://www.rubenwargner.com",
  defaultImage: "https://customer-assets.emergentagent.com/job_a38efa49-e796-4faa-a77b-25f4f0ea2e3e/artifacts/vx6ol7d8_avater.png",
  personalPhone: "+15124348560",
  companyPhone: "+12034963375",
  email: "ruben@redspire.com",
  linkedIn: "https://www.linkedin.com/in/ruben-wargner-59a83b336",
  companyUrl: "https://redspire-eng.com"
};

export const KEYWORDS = [
  "Ruben P. Wargner",
  "Redspire Civil Engineering",
  "Civil infrastructure strategist",
  "Infrastructure leadership",
  "Engineering projects",
  "Cross-continental engineering",
  "Structural engineering innovation",
  "Infrastructure delivery",
  "Bridge engineering",
  "Transit systems",
  "Digital twin technology",
  "Capital program governance"
];

// Person Schema for Ruben P. Wargner (Google Knowledge Panel Optimized)
export const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Ruben P. Wargner",
  "image": SITE_CONFIG.defaultImage,
  "jobTitle": "Founder & Chief Strategy Officer",
  "worksFor": {
    "@type": "Organization",
    "name": "Redspire Civil Engineering",
    "url": SITE_CONFIG.companyUrl
  },
  "email": SITE_CONFIG.email,
  "telephone": SITE_CONFIG.personalPhone,
  "url": SITE_CONFIG.siteUrl,
  "sameAs": [
    SITE_CONFIG.linkedIn,
    SITE_CONFIG.companyUrl,
    `${SITE_CONFIG.siteUrl}/#publications`
  ],
  "description": "Ruben P. Wargner is a Czech-American civil and structural engineering strategist known for leading multi-continent infrastructure programs across Europe and the United States. He is the Founder and Chief Strategy Officer of Redspire Civil Engineering, a consultancy recognized for long-range infrastructure planning, bridge and transit systems, and high-resilience public works. Based in Salt Lake City, Utah, Wargner is noted for advancing digital-twin modeling, cross-border engineering coordination, and safety-first project governance. Over his three-decade career, he has guided major capital programs, mentored international engineering teams, and contributed to infrastructure modernization efforts spanning highways, water systems, and urban mobility networks.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Salt Lake City",
    "addressRegion": "UT",
    "addressCountry": "US"
  },
  "knowsAbout": [
    "Civil Engineering",
    "Structural Engineering",
    "Infrastructure Strategy",
    "Bridge Systems",
    "Transit Networks",
    "Digital Twin Technology",
    "Capital Program Governance",
    "Cross-Border Engineering Coordination",
    "High-Resilience Public Works",
    "Infrastructure Modernization"
  ],
  "alumniOf": {
    "@type": "EducationalOrganization",
    "name": "Czech Technical University in Prague"
  },
  "award": [
    "International Bridge Conference Leadership Ribbon",
    "ASCE Outstanding Civil Engineer",
    "ENR Top 25 Newsmaker",
    "Czech-American Chamber Excellence Award"
  ]
};

// Organization Schema for Redspire Civil Engineering
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Redspire Civil Engineering",
  "url": SITE_CONFIG.companyUrl,
  "logo": SITE_CONFIG.defaultImage,
  "description": "Cross-continental civil engineering consultancy specializing in infrastructure modernization, predictive maintenance, bridge strengthening, transit expansion, and digital twin integration.",
  "foundingDate": "1998",
  "founder": {
    "@type": "Person",
    "name": "Ruben P. Wargner"
  },
  "address": [
    {
      "@type": "PostalAddress",
      "addressLocality": "Salt Lake City",
      "addressRegion": "UT",
      "addressCountry": "USA"
    },
    {
      "@type": "PostalAddress",
      "addressLocality": "Prague",
      "addressCountry": "Czech Republic"
    }
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": SITE_CONFIG.companyPhone,
    "contactType": "Business Inquiries",
    "email": SITE_CONFIG.email
  },
  "sameAs": [
    SITE_CONFIG.linkedIn
  ]
};

// Website Schema
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": SITE_CONFIG.siteName,
  "url": SITE_CONFIG.siteUrl,
  "description": "Official biography and professional profile of Ruben P. Wargner, Founder & Chief Strategy Officer of Redspire Civil Engineering.",
  "publisher": {
    "@type": "Organization",
    "name": "Redspire Civil Engineering"
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${SITE_CONFIG.siteUrl}/?search={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
};

// Article Schema Generator
export const generateArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description,
  "image": article.heroImage,
  "author": {
    "@type": "Person",
    "name": "Ruben P. Wargner",
    "url": SITE_CONFIG.siteUrl
  },
  "publisher": {
    "@type": "Organization",
    "name": "Redspire Civil Engineering",
    "logo": {
      "@type": "ImageObject",
      "url": SITE_CONFIG.defaultImage
    }
  },
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "articleSection": article.section,
  "url": `${SITE_CONFIG.siteUrl}${article.path}`,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${SITE_CONFIG.siteUrl}${article.path}`
  }
});

// Page-specific SEO data
export const PAGE_SEO = {
  home: {
    title: "Ruben P. Wargner | Founder & CSO, Redspire Civil Engineering",
    description: "Official biography of Ruben P. Wargner, Czech-American civil engineering strategist with 30+ years leading cross-continental infrastructure programs. Founder of Redspire Civil Engineering.",
    keywords: KEYWORDS.join(", "),
    ogType: "profile"
  },
  crossContinentalInfrastructure: {
    title: "The Future of Cross-Continental Infrastructure Networks | Ruben P. Wargner",
    description: "In-depth analysis of emerging trends in international infrastructure development, examining digital twins and cross-border collaboration reshaping the industry.",
    keywords: "cross-continental infrastructure, international engineering, digital twins, infrastructure networks, Ruben Wargner",
    ogType: "article",
    heroImage: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/i4vu97ud_Gemini_Generated_Image_cqtchrcqtchrcqtc.png",
    datePublished: "2023-06-15",
    section: "Infrastructure Strategy"
  },
  generationalThinking: {
    title: "Generational Thinking in Civil Engineering | Ruben P. Wargner",
    description: "Influential piece on designing infrastructure with 100-year horizons, challenging engineers to consider lifecycle impacts beyond standard design specifications.",
    keywords: "generational thinking, civil engineering philosophy, 100-year infrastructure, lifecycle design, Ruben Wargner",
    ogType: "article",
    heroImage: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/lgs66uw9_Gemini_Generated_Image_sz0vd4sz0vd4sz0v.png",
    datePublished: "2022-09-20",
    section: "Engineering Philosophy"
  },
  bridgingCultures: {
    title: "Bridging Cultures: European Innovation in American Infrastructure | Ruben P. Wargner",
    description: "Interview feature exploring how Czech engineering traditions and European regulatory frameworks influence modern American infrastructure projects.",
    keywords: "European engineering, American infrastructure, Czech engineering, cross-cultural engineering, Ruben Wargner",
    ogType: "article",
    heroImage: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/v2a3qn3p_Gemini_Generated_Image_dy2fnhdy2fnhdy2f.png",
    datePublished: "2021-11-10",
    section: "Global Engineering"
  },
  featuredRecognition: {
    title: "Featured Recognition: Infrastructure Leaders Review | Ruben P. Wargner",
    description: "Cover feature recognizing Ruben P. Wargner as a Global Infrastructure Visionary for cross-continental leadership and transformative impact on civil engineering.",
    keywords: "infrastructure leadership, engineering recognition, Global Infrastructure Visionary, Ruben Wargner award",
    ogType: "article",
    heroImage: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/c6a64o6y_Gemini_Generated_Image_zhkbzizhkbzizhkb.png",
    datePublished: "2023-07-01",
    section: "Recognition"
  },
  timeline: {
    title: "The Collective Journey | Ruben P. Wargner Timeline",
    description: "Explore three decades of Ruben P. Wargner's leadership through stories and testimonials from colleagues, mentors, and partners across Europe and the United States.",
    keywords: "Ruben Wargner timeline, engineering leadership journey, Redspire Collective Journey",
    ogType: "website",
    heroImage: SITE_CONFIG.defaultImage,
    path: "/timeline"
  }
};

export default {
  SITE_CONFIG,
  KEYWORDS,
  personSchema,
  organizationSchema,
  websiteSchema,
  generateArticleSchema,
  PAGE_SEO
};
